import { routing } from '@/i18n/routing';
import type { Locale } from 'next-intl';

const SUPPORTED_LOCALES = new Set(routing.locales as Locale[]);
const DEFAULT_LOCALE = routing.defaultLocale as Locale;
const SUPPORTED_LOCALE_BY_TAG = new Map<string, Locale>(
  [...SUPPORTED_LOCALES].map((locale) => [locale.toLowerCase(), locale])
);
const SUPPORTED_LOCALE_ALIASES: Record<string, Locale> = {
  'zh-cn': 'zh',
  'zh-sg': 'zh',
  'zh-hans': 'zh',
  'zh-tw': 'zh-hant',
  'zh-hk': 'zh-hant',
  'zh-mo': 'zh-hant',
  'zh-hant': 'zh-hant',
};

export function isSupportedLocale(locale?: string | null): locale is Locale {
  return !!locale && SUPPORTED_LOCALES.has(locale as Locale);
}

export function matchSupportedLocaleTag(locale?: string | null): Locale | null {
  if (!locale) return null;

  const normalized = locale.trim().replace(/_/g, '-').toLowerCase();
  if (!normalized) return null;

  const directMatch = SUPPORTED_LOCALE_BY_TAG.get(normalized);
  if (directMatch) return directMatch;

  const aliasMatch = SUPPORTED_LOCALE_ALIASES[normalized];
  if (aliasMatch) return aliasMatch;

  const baseTag = normalized.split('-')[0];
  if (!baseTag) return null;

  return (
    SUPPORTED_LOCALE_BY_TAG.get(baseTag) ??
    SUPPORTED_LOCALE_ALIASES[baseTag] ??
    null
  );
}

export function normalizeLocale(locale?: string | null): Locale | null {
  return matchSupportedLocaleTag(locale);
}

export function resolveLocaleWithFallback(
  locales: Array<string | null | undefined>
): Locale {
  for (const value of locales) {
    const locale = normalizeLocale(value ?? null);
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}
